import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Bot,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Loader2,
    MessageSquare,
    Search,
    ThumbsDown,
    ThumbsUp,
    User,
    X,
} from 'lucide-react';

import {
    adminDataApi,
    AdminLogItem,
    DebugAnswerDetail,
    DebugConversationRow,
} from '../../api/adminDataApi';
import userMetadataApi from '../../api/userMetadataApi';
import SearchableBusinessSelect from '../../components/SearchableBusinessSelect';
import { useAdminContext } from './AdminContext';
import { formatLocalTimestamp } from '../../utils/formatLocalTimestamp';
import stripeApi from '../../api/stripeApi';
import { useMutation } from '@tanstack/react-query';

type DebugTab = 'logs' | 'conversations' | 'llm_usage' | 'bank_ingest';
type InspectorTab = 'response' | 'usage' | 'identifiers' | 'revision' | 'routing' | 'timeline' | 'tools' | 'readiness';

const tabs: Array<{ id: DebugTab; label: string }> = [
    { id: 'logs', label: 'Logs' },
    { id: 'conversations', label: 'Conversations' },
    { id: 'llm_usage', label: 'LLM Usage' },
    { id: 'bank_ingest', label: 'Bank Ingest' },
];

const inspectorTabs: Array<{ id: InspectorTab; label: string }> = [
    { id: 'response', label: 'Final Response' },
    { id: 'usage', label: 'Usage' },
    { id: 'identifiers', label: 'Identifiers' },
    { id: 'revision', label: 'Revision' },
    { id: 'routing', label: 'Routing' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'tools', label: 'Tools' },
    { id: 'readiness', label: 'Readiness' },
];

const toPretty = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

const getPreferredAnswerPayload = (detail?: DebugAnswerDetail): unknown => {
    if (!detail) return '-';
    if (detail.answer_raw !== null && detail.answer_raw !== undefined && detail.answer_raw !== '') return detail.answer_raw;
    if (detail.answer_masked !== null && detail.answer_masked !== undefined && detail.answer_masked !== '') return detail.answer_masked;
    return '-';
};

const toNumber = (value: unknown): number => {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
};

const detailNumber = (row: AdminLogItem, key: string): number => toNumber(row.details?.[key]);
const detailText = (row: AdminLogItem, key: string): string => {
    const value = row.details?.[key];
    return value === null || value === undefined || value === '' ? '-' : String(value);
};

const getUsageSummary = (detail?: DebugAnswerDetail) => {
    const invocations = Array.isArray(detail?.llm_invocations) ? detail?.llm_invocations as Array<Record<string, unknown>> : [];
    type UsageTotals = {
        input_tokens: number;
        output_tokens: number;
        reasoning_tokens: number;
        cached_input_tokens: number;
        total_tokens: number;
        latency_ms: number;
    };
    const totals = invocations.reduce<UsageTotals>(
        (acc, invocation) => {
            acc.input_tokens += toNumber(invocation.input_tokens);
            acc.output_tokens += toNumber(invocation.output_tokens);
            acc.reasoning_tokens += toNumber(invocation.reasoning_tokens);
            acc.cached_input_tokens += toNumber(invocation.cached_input_tokens);
            const total = toNumber(invocation.total_tokens);
            acc.total_tokens += total || toNumber(invocation.input_tokens) + toNumber(invocation.output_tokens);
            acc.latency_ms += toNumber(invocation.latency_ms);
            return acc;
        },
        { input_tokens: 0, output_tokens: 0, reasoning_tokens: 0, cached_input_tokens: 0, total_tokens: 0, latency_ms: 0 },
    );
    return { invocations, totals };
};

const getAnswerIdFromTurn = (turn: Record<string, unknown>): string | null => {
    const answerMeta = (turn.answer_meta || {}) as Record<string, unknown>;
    const trace = (turn.linked_trace_summary || {}) as Record<string, unknown>;
    const candidate = (answerMeta.answer_id as string) || (trace.answer_id as string) || null;
    if (!candidate) return null;
    const normalized = String(candidate).trim();
    if (!normalized || normalized === 'null' || normalized === 'undefined') return null;
    return normalized;
};

const getTraceIdFromTurn = (turn: Record<string, unknown>): string | null => {
    const trace = (turn.linked_trace_summary || {}) as Record<string, unknown>;
    const candidate = (trace.trace_id as string) || null;
    if (!candidate) return null;
    const normalized = String(candidate).trim();
    if (!normalized || normalized === 'null' || normalized === 'undefined') return null;
    return normalized;
};

const normalizeRawBankTransactionsInput = (value: string): Record<string, any>[] => {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed as Record<string, any>[];
    if (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).transactions)) {
        return (parsed as any).transactions as Record<string, any>[];
    }
    if (parsed && typeof parsed === 'object') return [parsed as Record<string, any>];
    throw new Error('JSON must be a raw transaction object, an array of objects, or { "transactions": [...] }.');
};

const AdminSelect: React.FC<{ value: string; onChange: (value: string) => void; children: React.ReactNode; className?: string }> = ({
    value,
    onChange,
    children,
    className = 'w-[190px]',
}) => (
    <div className={`relative ${className}`}>
        <select
            value={value}
            onChange={(event) => onChange(event.target.value)}
            className="h-11 w-full appearance-none rounded-lg border border-gray-200 bg-white px-4 pr-10 text-sm font-medium text-gray-700 shadow-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        >
            {children}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
    </div>
);

const StatusPill: React.FC<{ value?: string | null }> = ({ value }) => {
    const normalized = String(value || 'ok').toLowerCase();
    const failed = ['error', 'failed', 'fail'].some((item) => normalized.includes(item));
    return (
        <span className={`rounded-md px-3 py-1 text-xs font-medium ${failed ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
            {failed ? 'Failed' : 'Success'}
        </span>
    );
};

const RatingPill: React.FC<{ badCount?: number }> = ({ badCount = 0 }) => {
    if (badCount > 0) return <span className="rounded-md bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700">Bad</span>;
    return <span className="rounded-md bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">Undefined</span>;
};

const AdminDebugConsole: React.FC = () => {
    const { selectedBusinessId, setSelectedBusinessId } = useAdminContext();
    const [activeTab, setActiveTab] = useState<DebugTab>('logs');
    const [days, setDays] = useState(7);
    const [limit, setLimit] = useState(100);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [routeFilter, setRouteFilter] = useState('');
    const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
    const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(null);
    const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
    const [inspectorOpen, setInspectorOpen] = useState(false);
    const [inspectorTab, setInspectorTab] = useState<InspectorTab>('response');
    const [rawBankJson, setRawBankJson] = useState('');
    const [rawBankMessage, setRawBankMessage] = useState<string | null>(null);
    const [rawBankMessageType, setRawBankMessageType] = useState<'info' | 'success' | 'error'>('info');

    const scopedBusinessId = selectedBusinessId.trim() || undefined;
    const scopedSearch = search.trim() || undefined;

    const { data: allBusinessesData } = useQuery({
        queryKey: ['allBusinesses'],
        queryFn: userMetadataApi.getAllBusinesses,
    });

    const businessOptions = useMemo(() => {
        return [
            { id: '', name: 'Business: All' },
            ...(allBusinessesData?.businesses || []).map((business) => ({
                id: business.business_id || business.user_id,
                name: business.legal_name || business.dba_name || business.onboarding?.qbo_metadata?.company_name || business.user_email || 'Unknown business',
                subtitle: business.user_email || undefined,
            })),
        ];
    }, [allBusinessesData]);

    const logsQuery = useQuery({
        queryKey: ['admin-debug-logs', scopedBusinessId, days, scopedSearch, statusFilter, limit],
        queryFn: () =>
            adminDataApi.getLogs({
                business_id: scopedBusinessId,
                days,
                source: 'all',
                status: statusFilter || undefined,
                limit,
                search: scopedSearch,
            }),
        enabled: activeTab === 'logs',
    });

    const conversationsQuery = useQuery({
        queryKey: ['admin-debug-conversations', scopedBusinessId, days, scopedSearch, routeFilter, statusFilter, limit],
        queryFn: () =>
            adminDataApi.getDebugConversations({
                business_id: scopedBusinessId,
                days,
                search: scopedSearch,
                route_type: routeFilter || undefined,
                status: statusFilter || undefined,
                has_bad_feedback: false,
                limit,
            }),
        enabled: activeTab === 'conversations',
    });

    const llmUsageQuery = useQuery({
        queryKey: ['admin-debug-llm-usage', scopedBusinessId, days, scopedSearch, routeFilter, statusFilter, limit],
        queryFn: () =>
            adminDataApi.getLogs({
                business_id: scopedBusinessId,
                days,
                source: 'llm_audit',
                status: statusFilter || undefined,
                action_prefix: routeFilter || undefined,
                limit,
                search: scopedSearch,
            }),
        enabled: activeTab === 'llm_usage',
    });

    const conversationRows = useMemo(
        () => ((conversationsQuery.data?.items || []) as DebugConversationRow[]),
        [conversationsQuery.data?.items],
    );

    const logRows = useMemo(() => ((logsQuery.data?.items || []) as AdminLogItem[]), [logsQuery.data?.items]);
    const llmUsageRows = useMemo(() => ((llmUsageQuery.data?.items || []) as AdminLogItem[]), [llmUsageQuery.data?.items]);

    const conversationDetailQuery = useQuery({
        queryKey: ['admin-debug-conversation-detail', selectedConversationId, scopedBusinessId],
        queryFn: () => adminDataApi.getDebugConversation(selectedConversationId as string, true, scopedBusinessId),
        enabled: Boolean(selectedConversationId),
        retry: (failureCount, error: any) => {
            const status = error?.response?.status;
            if (status === 404) return false;
            return failureCount < 2;
        },
    });

    const answerDetailQuery = useQuery({
        queryKey: ['admin-debug-answer-detail', selectedAnswerId, selectedTraceId],
        queryFn: () =>
            selectedAnswerId
                ? adminDataApi.getDebugAnswerDetail(selectedAnswerId, true)
                : adminDataApi.getDebugTraceDetail(selectedTraceId as string, true),
        enabled: inspectorOpen && Boolean(selectedAnswerId || selectedTraceId),
        retry: (failureCount, error: any) => {
            const status = error?.response?.status;
            if (status === 404) return false;
            return failureCount < 2;
        },
    });

    const parsedRawBankTransactions = useMemo(() => {
        if (!rawBankJson.trim()) return [];
        try {
            return normalizeRawBankTransactionsInput(rawBankJson);
        } catch {
            return [];
        }
    }, [rawBankJson]);

    const manualIngestRawBankMutation = useMutation({
        mutationFn: async () => {
            const transactions = normalizeRawBankTransactionsInput(rawBankJson);
            return stripeApi.manualIngestTransactions(transactions, scopedBusinessId);
        },
        onSuccess: (result) => {
            setRawBankMessageType('success');
            setRawBankMessage(`Success: ingested ${result?.transaction_count || 0} raw transaction(s) to Kafka.`);
        },
        onError: (error: any) => {
            setRawBankMessageType('error');
            setRawBankMessage(error?.response?.data?.detail || error?.message || 'Failed to ingest raw bank JSON.');
        },
    });

    const selectedConversation = conversationRows.find((row) => row.conversation_id === selectedConversationId) || conversationRows[0];
    const turns = conversationDetailQuery.data?.turns || [];

    useEffect(() => {
        if (selectedConversationId || !conversationRows.length) return;
        setSelectedConversationId(conversationRows[0].conversation_id);
    }, [conversationRows, selectedConversationId]);

    const refresh = () => {
        if (activeTab === 'logs') logsQuery.refetch();
        if (activeTab === 'llm_usage') llmUsageQuery.refetch();
        if (activeTab === 'conversations') {
            conversationsQuery.refetch();
            conversationDetailQuery.refetch();
        }
    };

    const resetFilters = () => {
        setSearch('');
        setStatusFilter('');
        setRouteFilter('');
        setSelectedBusinessId('');
    };

    const openInspector = (turn: Record<string, unknown>) => {
        const answerId = getAnswerIdFromTurn(turn);
        const traceId = getTraceIdFromTurn(turn);
        if (!answerId && !traceId) return;
        setSelectedAnswerId(answerId);
        setSelectedTraceId(traceId);
        setInspectorTab('response');
        setInspectorOpen(true);
    };

    const renderInspectorContent = () => {
        const detail = answerDetailQuery.data as DebugAnswerDetail | undefined;
        if (answerDetailQuery.isLoading) {
            return <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;
        }
        if (!detail) {
            return <div className="text-sm text-gray-500">Select an assistant response to inspect details.</div>;
        }
        if (inspectorTab === 'response') return <InspectorBlock title="Final Response" value={getPreferredAnswerPayload(detail)} />;
        if (inspectorTab === 'usage') return <UsageInspector detail={detail} />;
        if (inspectorTab === 'identifiers') {
            return (
                <InspectorBlock
                    title="Identifiers"
                    value={{
                        answer_id: detail.answer_id || selectedAnswerId,
                        conversation_id: detail.conversation_id,
                        answer_message_id: detail.answer_message_id,
                        request_id: detail.request_id,
                        invocation_id: detail.primary_invocation_id,
                        route_type: ((detail.routing || {}) as Record<string, unknown>).route_type,
                        template_id: ((detail.routing || {}) as Record<string, unknown>).template_id,
                        route_confidence: ((detail.routing || {}) as Record<string, unknown>).route_confidence,
                        cache_metric: ((detail.routing || {}) as Record<string, unknown>).cache_metric,
                        trace_id: detail.trace_id || selectedTraceId,
                        trace_completeness: detail.trace_completeness,
                        status: detail.status,
                    }}
                />
            );
        }
        if (inspectorTab === 'revision') return <InspectorBlock title="Revisions" value={detail.revisions_snapshot || []} />;
        if (inspectorTab === 'routing') return <InspectorBlock title="Routing Snapshot" value={detail.routing} />;
        if (inspectorTab === 'timeline') return <InspectorBlock title="Timeline" value={detail.timeline || []} />;
        if (inspectorTab === 'tools') return <InspectorBlock title="Tool Results" value={detail.tool_results_summary || []} />;
        return (
            <InspectorBlock
                title="Readiness / Grounding"
                value={{
                    data_readiness_snapshot: detail.data_readiness_snapshot,
                    grounding_check: detail.grounding_check,
                    response_numbers: detail.response_numbers,
                }}
            />
        );
    };

    return (
        <div className="relative flex h-full flex-col gap-7 overflow-hidden bg-gray-50 px-6 py-6">
            <div className="flex items-start justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Debug</h1>
                {activeTab !== 'bank_ingest' && (
                    <button
                        onClick={refresh}
                        className="h-12 rounded-lg bg-indigo-500 px-8 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600"
                    >
                        Refresh
                    </button>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
                {activeTab !== 'bank_ingest' && (
                    <div className="relative min-w-[320px] flex-1 max-w-[470px]">
                        <input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search business name, client name, email,..."
                            className="h-11 w-full rounded-lg border border-gray-200 bg-white pl-4 pr-11 text-sm italic text-gray-700 shadow-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        />
                        <Search className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
                    </div>
                )}

                {activeTab !== 'bank_ingest' && (
                    <AdminSelect value={statusFilter} onChange={setStatusFilter}>
                        <option value="">Status: All</option>
                        <option value="ok">Success</option>
                        <option value="error">Failed</option>
                        <option value="complete">Complete</option>
                    </AdminSelect>
                )}

                <SearchableBusinessSelect
                    options={businessOptions}
                    value={selectedBusinessId}
                    onChange={setSelectedBusinessId}
                    placeholder="Business: All"
                    widthClassName={activeTab === 'bank_ingest' ? 'w-[320px]' : 'w-[240px]'}
                    showSubtitleWhenClosed={false}
                    buttonClassName="relative h-11 w-full rounded-lg border border-gray-200 bg-white py-0 pl-4 pr-10 text-left text-sm font-medium text-gray-700 shadow-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    menuClassName="absolute top-12 left-0 z-50 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                />

                <button onClick={resetFilters} className="h-11 px-4 text-sm font-medium text-indigo-500 hover:text-indigo-700">
                    Reset
                </button>

                {activeTab !== 'bank_ingest' && (
                    <div className="ml-auto flex items-center gap-3">
                        <AdminSelect value={String(days)} onChange={(value) => setDays(Number(value))} className="w-[180px]">
                            <option value="1">Last 24 hours</option>
                            <option value="7">Last 7 days</option>
                            <option value="30">Last 30 days</option>
                            <option value="90">Last 90 days</option>
                        </AdminSelect>
                        <AdminSelect value={String(limit)} onChange={(value) => setLimit(Number(value))} className="w-[160px]">
                            <option value="50">50 rows</option>
                            <option value="100">100 rows</option>
                            <option value="200">200 rows</option>
                        </AdminSelect>
                    </div>
                )}
            </div>

            <div className="flex items-center gap-8">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`border-b-2 pb-3 text-sm font-semibold transition-colors ${
                            activeTab === tab.id
                                ? 'border-indigo-500 text-gray-900'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-900'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'logs' && (
                <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-white">
                    {logsQuery.isLoading ? (
                        <div className="flex h-full items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                        </div>
                    ) : (
                        <div className="flex h-full flex-col">
                            <div className="min-h-0 flex-1 overflow-auto">
                                <table className="min-w-full text-left text-sm">
                                    <thead className="sticky top-0 border-b border-gray-200 bg-white text-gray-600">
                                        <tr>
                                            <th className="px-8 py-5 font-medium">Time</th>
                                            <th className="px-8 py-5 font-medium">Event</th>
                                            <th className="px-8 py-5 font-medium">Status</th>
                                            <th className="px-8 py-5 font-medium">User</th>
                                            <th className="px-8 py-5 font-medium">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {logRows.map((row) => (
                                            <tr key={row.id} className="hover:bg-gray-50">
                                                <td className="whitespace-nowrap px-8 py-4 text-gray-700">{formatLocalTimestamp(row.timestamp)}</td>
                                                <td className="px-8 py-4">
                                                    <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">{row.source}</span>
                                                </td>
                                                <td className="px-8 py-4"><StatusPill value={row.status} /></td>
                                                <td className="px-8 py-4 text-gray-700">{row.business_id || scopedBusinessId || '-'}</td>
                                                <td className="px-8 py-4 text-gray-800">{row.summary}</td>
                                            </tr>
                                        ))}
                                        {logRows.length === 0 && (
                                            <tr>
                                                <td colSpan={5} className="px-8 py-14 text-center text-gray-500">No logs found for current filters.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <PaginationFooter count={logRows.length} total={logsQuery.data?.total || logRows.length} />
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'llm_usage' && (
                <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-white">
                    {llmUsageQuery.isLoading ? (
                        <div className="flex h-full items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                        </div>
                    ) : (
                        <div className="flex h-full flex-col">
                            <div className="min-h-0 flex-1 overflow-auto">
                                <table className="min-w-full text-left text-sm">
                                    <thead className="sticky top-0 border-b border-gray-200 bg-white text-gray-600">
                                        <tr>
                                            <th className="px-6 py-5 font-medium">Time</th>
                                            <th className="px-6 py-5 font-medium">Task</th>
                                            <th className="px-6 py-5 font-medium">Model</th>
                                            <th className="px-6 py-5 font-medium">Tokens</th>
                                            <th className="px-6 py-5 font-medium">Latency</th>
                                            <th className="px-6 py-5 font-medium">Business</th>
                                            <th className="px-6 py-5 font-medium">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {llmUsageRows.map((row) => {
                                            const input = detailNumber(row, 'input_tokens');
                                            const output = detailNumber(row, 'output_tokens');
                                            const reasoning = detailNumber(row, 'reasoning_tokens');
                                            const cached = detailNumber(row, 'cached_input_tokens');
                                            const total = detailNumber(row, 'total_tokens') || input + output + reasoning;
                                            return (
                                                <tr key={row.id} className="hover:bg-gray-50">
                                                    <td className="whitespace-nowrap px-6 py-4 text-gray-700">{formatLocalTimestamp(row.timestamp)}</td>
                                                    <td className="px-6 py-4">
                                                        <div className="font-medium text-gray-800">{detailText(row, 'route_type')}</div>
                                                        <div className="mt-1 max-w-[360px] truncate text-xs text-gray-500">{detailText(row, 'masked_query_input')}</div>
                                                    </td>
                                                    <td className="max-w-[220px] truncate px-6 py-4 text-gray-700">{detailText(row, 'model')}</td>
                                                    <td className="px-6 py-4 text-gray-700">
                                                        <div className="font-medium text-gray-900">{total.toLocaleString()} total</div>
                                                        <div className="mt-1 text-xs text-gray-500">
                                                            in {input.toLocaleString()} | out {output.toLocaleString()} | reasoning {reasoning.toLocaleString()} | cached {cached.toLocaleString()}
                                                        </div>
                                                    </td>
                                                    <td className="whitespace-nowrap px-6 py-4 text-gray-700">{detailNumber(row, 'latency_ms').toLocaleString()} ms</td>
                                                    <td className="max-w-[180px] truncate px-6 py-4 text-gray-700">{row.business_id || '-'}</td>
                                                    <td className="px-6 py-4"><StatusPill value={row.status} /></td>
                                                </tr>
                                            );
                                        })}
                                        {llmUsageRows.length === 0 && (
                                            <tr>
                                                <td colSpan={7} className="px-8 py-14 text-center text-gray-500">No LLM usage found for current filters.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <PaginationFooter count={llmUsageRows.length} total={llmUsageQuery.data?.total || llmUsageRows.length} />
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'conversations' && (
                <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(420px,0.95fr)]">
                    <div className="min-h-0 overflow-hidden rounded-xl border border-gray-200 bg-white">
                        <div className="h-full overflow-auto">
                            <table className="min-w-full text-left text-sm">
                                <thead className="sticky top-0 border-b border-gray-200 bg-white text-gray-600">
                                    <tr>
                                        <th className="px-8 py-5 font-medium">Conversation</th>
                                        <th className="px-8 py-5 font-medium">User</th>
                                        <th className="px-8 py-5 font-medium">Route</th>
                                        <th className="px-8 py-5 font-medium">Rating</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {conversationRows.map((row) => (
                                        <tr
                                            key={row.conversation_id}
                                            onClick={() => setSelectedConversationId(row.conversation_id)}
                                            className={`cursor-pointer hover:bg-gray-50 ${selectedConversation?.conversation_id === row.conversation_id ? 'bg-gray-50' : ''}`}
                                        >
                                            <td className="px-8 py-4">
                                                <div className="font-medium text-gray-800">{row.title || 'Chat about signals'}</div>
                                                <div className="mt-1 text-xs text-gray-500">{formatLocalTimestamp(row.last_trace_at || row.last_updated || null)}</div>
                                            </td>
                                            <td className="px-8 py-4">
                                                <div className="font-medium text-gray-800">{row.business_id || 'Mark W'}</div>
                                                <div className="mt-1 max-w-[190px] truncate text-xs text-gray-500">{row.conversation_id}</div>
                                            </td>
                                            <td className="px-8 py-4">
                                                <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                                                    <MessageSquare className="h-3 w-3" />
                                                    {row.latest_route_type || 'Signal'}
                                                </span>
                                            </td>
                                            <td className="px-8 py-4"><RatingPill badCount={row.bad_feedback_count} /></td>
                                        </tr>
                                    ))}
                                    {conversationRows.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="px-8 py-14 text-center text-gray-500">No conversations found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="min-h-0 overflow-hidden rounded-xl bg-gray-100 p-7">
                        {selectedConversation ? (
                            <div className="flex h-full flex-col">
                                <div className="mb-6 flex items-center justify-between gap-4">
                                    <h2 className="text-lg font-semibold text-gray-700">
                                        {selectedConversation.title || 'Chat about signals'} · {conversationDetailQuery.data?.message_count || selectedConversation.message_count || 0} messages
                                    </h2>
                                    <AdminSelect value="" onChange={() => undefined} className="w-[240px]">
                                        <option value="">Select Rating</option>
                                        <option value="good">Good</option>
                                        <option value="bad">Bad</option>
                                    </AdminSelect>
                                </div>

                                <div className="min-h-0 flex-1 space-y-5 overflow-auto pr-1">
                                    {conversationDetailQuery.isLoading ? (
                                        <div className="flex h-48 items-center justify-center">
                                            <Loader2 className="h-7 w-7 animate-spin text-indigo-500" />
                                        </div>
                                    ) : turns.length ? (
                                        turns.map((turn, index) => {
                                            const role = String(turn.role || 'unknown');
                                            const isAssistant = role === 'assistant';
                                            const key = String(turn.message_id || `${role}-${index}`);
                                            return (
                                                <div key={key} className="rounded-lg border border-gray-200 bg-white p-5">
                                                    <div className="mb-5 flex items-center justify-between gap-3">
                                                        <div className="flex items-center gap-3 text-xs text-gray-400">
                                                            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-medium ${
                                                                isAssistant ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-100 text-gray-700'
                                                            }`}>
                                                                {isAssistant ? <Bot className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                                                                {isAssistant ? 'Assistant' : 'User'}
                                                            </span>
                                                            <span>{formatLocalTimestamp(turn.timestamp as string)}</span>
                                                        </div>
                                                        {isAssistant && (
                                                            <div className="flex items-center gap-3 text-gray-400">
                                                                <ThumbsUp className="h-5 w-5" />
                                                                <ThumbsDown className="h-5 w-5" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="whitespace-pre-wrap text-sm leading-6 text-gray-700">{toPretty(turn.content)}</div>
                                                    {isAssistant && (
                                                        <div className="mt-5 text-right">
                                                            <button
                                                                onClick={() => openInspector(turn as Record<string, unknown>)}
                                                                className="text-sm font-medium text-indigo-500 hover:text-indigo-700"
                                                            >
                                                                Inspect details -&gt;
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-500">
                                            Select a conversation to inspect full turns and linked traces.
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-500">
                                Select a conversation to inspect full turns and linked traces.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'bank_ingest' && (
                <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-gray-200 bg-white p-6">
                    <div className="mb-4 flex items-start justify-between gap-4">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Raw Bank Transaction Test Ingest</h2>
                            <p className="mt-1 max-w-3xl text-sm text-gray-500">
                                Paste a single bank raw transaction object, an array of objects, or a transactions wrapper.
                            </p>
                        </div>
                        <div className="text-sm text-gray-500">
                            Business: <span className="font-medium text-gray-800">{scopedBusinessId || 'None selected'}</span>
                        </div>
                    </div>
                    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_280px]">
                        <textarea
                            value={rawBankJson}
                            onChange={(event) => {
                                setRawBankJson(event.target.value);
                                setRawBankMessage(null);
                                setRawBankMessageType('info');
                            }}
                            placeholder="Paste raw bank transaction JSON here"
                            className="min-h-[360px] rounded-lg border border-gray-200 px-4 py-3 font-mono text-xs outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        />
                        <div className="space-y-3">
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                                <div className="font-medium text-gray-800">Parsed Summary</div>
                                <div className="mt-2">Transactions: {parsedRawBankTransactions.length}</div>
                            </div>
                            <button
                                onClick={() => manualIngestRawBankMutation.mutate()}
                                disabled={!scopedBusinessId || !rawBankJson.trim() || manualIngestRawBankMutation.isPending}
                                className="w-full rounded-lg bg-indigo-500 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-600 disabled:opacity-50"
                            >
                                {manualIngestRawBankMutation.isPending ? 'Ingesting...' : 'Ingest Raw Bank JSON'}
                            </button>
                            <button
                                onClick={() => {
                                    setRawBankJson('');
                                    setRawBankMessage(null);
                                }}
                                className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Clear
                            </button>
                            {rawBankMessage && (
                                <div className={`rounded-lg px-4 py-3 text-sm ${
                                    rawBankMessageType === 'success'
                                        ? 'bg-emerald-50 text-emerald-700'
                                        : rawBankMessageType === 'error'
                                            ? 'bg-rose-50 text-rose-700'
                                            : 'bg-gray-50 text-gray-700'
                                }`}>
                                    {rawBankMessage}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {inspectorOpen && (
                <div className="absolute inset-0 z-40 flex">
                    <div className="flex-1 bg-gray-900/20" onClick={() => setInspectorOpen(false)} />
                    <aside className="h-full w-full max-w-[820px] overflow-hidden border-l border-gray-200 bg-white shadow-2xl">
                        <div className="flex h-full flex-col">
                            <div className="flex items-center justify-between px-8 py-6">
                                <h2 className="text-lg font-semibold text-gray-900">Answer Inspector</h2>
                                <button onClick={() => setInspectorOpen(false)} className="rounded-md p-2 text-gray-500 hover:bg-gray-100">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="flex gap-7 overflow-x-auto px-8">
                                {inspectorTabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setInspectorTab(tab.id)}
                                        className={`shrink-0 border-b-2 pb-3 text-sm font-semibold ${
                                            inspectorTab === tab.id
                                                ? 'border-indigo-500 text-gray-900'
                                                : 'border-transparent text-gray-500 hover:text-gray-900'
                                        }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                            <div className="min-h-0 flex-1 overflow-auto px-8 py-6">
                                {renderInspectorContent()}
                            </div>
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
};

const PaginationFooter: React.FC<{ count: number; total: number }> = ({ count, total }) => (
    <div className="flex items-center justify-between border-t border-gray-200 px-8 py-4 text-sm text-gray-500">
        <div>{count ? `1-${count} out of ${total} results` : '0 results'}</div>
        <div className="flex items-center gap-4 text-gray-800">
            <button className="rounded-lg bg-gray-50 p-2 text-gray-300"><ChevronLeft className="h-4 w-4" /></button>
            <span className="font-medium">Page 1 of 1</span>
            <button className="rounded-lg bg-gray-50 p-2 text-gray-300"><ChevronRight className="h-4 w-4" /></button>
        </div>
    </div>
);

const InspectorBlock: React.FC<{ title: string; value: unknown }> = ({ title, value }) => (
    <section className="mb-7">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">{title}</h3>
        <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-5 font-mono text-xs leading-6 text-gray-800">
            {toPretty(value)}
        </pre>
    </section>
);

const UsageMetric: React.FC<{ label: string; value: number }> = ({ label, value }) => (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
        <div className="mt-2 text-2xl font-semibold text-gray-900">{value.toLocaleString()}</div>
    </div>
);

const UsageInspector: React.FC<{ detail: DebugAnswerDetail }> = ({ detail }) => {
    const { invocations, totals } = getUsageSummary(detail);
    return (
        <div className="space-y-7">
            <section>
                <h3 className="mb-3 text-sm font-semibold text-gray-900">Token Usage</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <UsageMetric label="Total Tokens" value={totals.total_tokens} />
                    <UsageMetric label="Input Tokens" value={totals.input_tokens} />
                    <UsageMetric label="Output Tokens" value={totals.output_tokens} />
                    <UsageMetric label="Reasoning Tokens" value={totals.reasoning_tokens} />
                    <UsageMetric label="Cached Input Tokens" value={totals.cached_input_tokens} />
                    <UsageMetric label="Latency (ms)" value={totals.latency_ms} />
                </div>
            </section>

            <section>
                <h3 className="mb-3 text-sm font-semibold text-gray-900">LLM Invocations</h3>
                <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="min-w-full text-left text-xs">
                        <thead className="bg-gray-50 text-gray-500">
                            <tr>
                                <th className="px-4 py-3 font-semibold">Model</th>
                                <th className="px-4 py-3 font-semibold">Route</th>
                                <th className="px-4 py-3 font-semibold">Input</th>
                                <th className="px-4 py-3 font-semibold">Output</th>
                                <th className="px-4 py-3 font-semibold">Reasoning</th>
                                <th className="px-4 py-3 font-semibold">Cached</th>
                                <th className="px-4 py-3 font-semibold">Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {invocations.map((invocation, index) => {
                                const input = toNumber(invocation.input_tokens);
                                const output = toNumber(invocation.output_tokens);
                                const total = toNumber(invocation.total_tokens) || input + output;
                                return (
                                    <tr key={String(invocation.invocation_id || index)}>
                                        <td className="max-w-[180px] truncate px-4 py-3 text-gray-800">{String(invocation.model || '-')}</td>
                                        <td className="px-4 py-3 text-gray-600">{String(invocation.route_type || '-')}</td>
                                        <td className="px-4 py-3 text-gray-800">{input.toLocaleString()}</td>
                                        <td className="px-4 py-3 text-gray-800">{output.toLocaleString()}</td>
                                        <td className="px-4 py-3 text-gray-800">{toNumber(invocation.reasoning_tokens).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-gray-800">{toNumber(invocation.cached_input_tokens).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-gray-800">{total.toLocaleString()}</td>
                                    </tr>
                                );
                            })}
                            {invocations.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                                        No LLM token usage was recorded for this response.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <InspectorBlock title="Raw Usage Records" value={invocations} />
        </div>
    );
};

export default AdminDebugConsole;
